import type { jsPDF } from 'jspdf';
import { cellTextOf, cellValue, shownTextOf } from './model';
import { buildExportSheet, type ExportInput, type ExportedSheet } from './exportExcel';
import type { AppGridColumn } from './types';

/**
 * PDF export for AppGrid, drawn into a jsPDF document the screen creates (the
 * way DevExtreme's pdf_exporter took `jsPDFDocument`), so the screen keeps its
 * page set-up and file name:
 *
 *   const doc = new jsPDF({ orientation: 'landscape', format: 'letter' });
 *   gridRef.current.exportToPdf(doc);
 *   doc.save('ServedItems.pdf');
 *
 * It writes what the Excel export writes (visible columns in their order, rows
 * after search / filters / sort, every group with its caption and summaries,
 * collapsed or not, and the total row), as the TEXT the grid shows: formats,
 * lookups and customizeText applied. Booleans print as "true" / "false", as
 * the DevExtreme PDF export printed them. Text too long for its column wraps
 * onto more lines in the cell; nothing is cut off. Columns keep their on-screen
 * proportions across the page width; the header repeats on every page.
 */

/** Text of one data cell as the PDF prints it. */
export function pdfCellText<T>(c: AppGridColumn<T>, row: T): string {
  if (c.calculateDisplayValue) return shownTextOf(c, row);
  const v = cellValue(c, row);
  if (typeof v === 'boolean' && !c.lookup && !c.customizeText && typeof c.format !== 'function') return String(v);
  if (!c.dataField && !c.calculateCellValue) return '';
  return cellTextOf(c, v);
}

/** The sheet with every value as the text the PDF draws (what the tests check). */
export function buildPdfSheet<T>(input: ExportInput<T>): ExportedSheet {
  const sheet = buildExportSheet(input);
  // buildExportSheet keeps data values typed (for Excel); redo data rows as text.
  const dataRows: T[] = [];
  const walk = (nodes: ExportInput<T>['nodes']) => {
    for (const n of nodes) {
      if (n.kind === 'data') dataRows.push(n.row);
      else walk(n.children);
    }
  };
  walk(input.nodes);
  let next = 0;
  const rows = sheet.rows.map((r) => {
    if (r.kind !== 'data') return { ...r, values: r.values.map((v) => (v == null ? '' : String(v))) };
    const row = dataRows[next++];
    return { ...r, values: input.columns.map((c) => pdfCellText(c, row)) };
  });
  return { header: sheet.header, rows };
}

export interface PdfLayout {
  /** On-screen widths of the columns, px (proportions only). */
  widths: number[];
  /** Font size, pt. Default 9. */
  fontSize?: number;
  /** Page margin, pt. Default 28 (about 10 mm). */
  margin?: number;
  /** Where the first page's header row starts, pt from the top edge (under a title the screen drew). Default: the margin. */
  top?: number;
}

/** Draws the grid into `doc` (from its current page on) and returns what it drew. */
export function drawGridPdf<T>(doc: jsPDF, input: ExportInput<T>, layout: PdfLayout): ExportedSheet {
  const sheet = buildPdfSheet(input);
  const k = doc.internal.scaleFactor; // pt per document unit
  const pt = (n: number) => n / k;
  const fontSize = layout.fontSize ?? 9;
  const margin = pt(layout.margin ?? 28);
  const pad = pt(3);
  const lineH = pt(fontSize * 1.2);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const avail = pageW - margin * 2;
  const total = layout.widths.reduce((s, w) => s + Math.max(1, w), 0) || 1;
  const widths = input.columns.map((_, i) => (Math.max(1, layout.widths[i] ?? 100) / total) * avail);
  const xs: number[] = [];
  widths.forEach((_, i) => xs.push(i === 0 ? margin : xs[i - 1] + widths[i - 1]));
  const align = (c: AppGridColumn<T>) => c.alignment ?? (c.dataType === 'number' ? 'right' : 'left');

  doc.setFontSize(fontSize);
  doc.setLineWidth(pt(0.5));
  doc.setDrawColor(200, 200, 200);
  let y = layout.top != null ? Math.max(margin, pt(layout.top)) : margin;

  const wrap = (text: string, width: number): string[] => (text === '' ? [] : (doc.splitTextToSize(text, Math.max(pt(4), width - pad * 2)) as string[]));

  const cellLines = (lines: string[], x: number, width: number, a: 'left' | 'center' | 'right', top: number) => {
    lines.forEach((line, li) => {
      const ty = top + pad + li * lineH;
      const tx = a === 'right' ? x + width - pad : a === 'center' ? x + width / 2 : x + pad;
      doc.text(line, tx, ty, { baseline: 'top', align: a });
    });
  };

  const drawRow = (values: string[], kind: 'header' | 'group' | 'data' | 'total') => {
    doc.setFont('helvetica', kind === 'data' ? 'normal' : 'bold');
    // A group caption spans the row; other rows have a cell per column.
    const cells = kind === 'group'
      ? [{ text: values[0] ?? '', x: margin, width: avail, a: 'left' as const }]
      : values.map((text, i) => ({ text, x: xs[i], width: widths[i], a: align(input.columns[i]) }));
    const wrapped = cells.map((c) => wrap(c.text, c.width));
    const h = Math.max(1, ...wrapped.map((l) => l.length)) * lineH + pad * 2;
    if (y + h > pageH - margin && kind !== 'header') {
      doc.addPage();
      y = margin;
      drawRow(sheet.header, 'header');
      doc.setFont('helvetica', kind === 'data' ? 'normal' : 'bold');
    }
    if (kind === 'group') {
      doc.setFillColor(242, 242, 242);
      doc.rect(margin, y, avail, h, 'F');
    }
    cells.forEach((c, i) => cellLines(wrapped[i], c.x, c.width, c.a, y));
    y += h;
    if (kind === 'header' || kind === 'total') doc.setDrawColor(120, 120, 120);
    doc.line(margin, kind === 'total' ? y - h : y, margin + avail, kind === 'total' ? y - h : y);
    doc.setDrawColor(200, 200, 200);
  };

  drawRow(sheet.header, 'header');
  for (const r of sheet.rows) drawRow(r.values as string[], r.kind);
  doc.setFont('helvetica', 'normal');
  return sheet;
}
